import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, Plug, Lock } from "@phosphor-icons/react";

const PROVIDERS = [
    {
        id: "Apify",
        name: "Apify",
        kind: "Ingestion",
        active: true,
        instructions: "Sign in at apify.com → Settings → API & Integrations → API tokens. Free tier includes monthly compute credits sufficient for testing.",
    },
    {
        id: "OpenAI",
        name: "OpenAI",
        kind: "AI Provider",
        instructions: "Currently routed through the Emergent universal LLM key. Add your own key here only if you want direct billing.",
    },
    {
        id: "Anthropic",
        name: "Claude (Anthropic)",
        kind: "AI Provider",
        instructions: "Default model: Claude Sonnet 4.5. Routed through the Emergent universal LLM key.",
    },
    {
        id: "Reddit",
        name: "Reddit API (optional)",
        kind: "Direct Platform",
        instructions: "Optional fallback for direct Reddit access without Apify. Not required for Reddit URL ingestion.",
    },
    {
        id: "TikTok",
        name: "TikTok",
        kind: "Direct Platform",
        instructions: "TikTok scraping is handled by Apify's TikTok actor. Direct API requires TikTok Research API approval.",
    },
    {
        id: "Instagram",
        name: "Instagram",
        kind: "Direct Platform",
        instructions: "Routed via Apify's Instagram comment scraper.",
    },
    {
        id: "YouTube",
        name: "YouTube",
        kind: "Direct Platform",
        instructions: "Routed via Apify's YouTube scraper. A direct YouTube Data API key can be added here later.",
    },
    {
        id: "App Store",
        name: "App Store Reviews",
        kind: "Reviews",
        instructions: "Public reviews via Apify's App Store actor.",
    },
    {
        id: "Google Play",
        name: "Google Play Reviews",
        kind: "Reviews",
        instructions: "Public reviews via Apify's Google Play actor.",
    },
];

function ProviderCard({ p, saved, onSave, onTest }) {
    const [apiKey, setApiKey] = useState("");
    const [active, setActive] = useState(saved?.active ?? p.active ?? true);
    const isApify = p.id === "Apify";

    return (
        <article data-testid={`integration-card-${p.id}`} className="border border-border bg-card p-6 hover-lift">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="overline mb-1.5">{p.kind}</div>
                    <h3 className="font-display font-semibold text-lg leading-tight flex items-center gap-2">
                        <Plug size={16} weight="duotone" /> {p.name}
                    </h3>
                </div>
                {saved && (
                    <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
                        <CheckCircle size={10} weight="duotone" className="mr-1" /> connected
                    </Badge>
                )}
            </div>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{p.instructions}</p>

            <div className="mt-4 space-y-3">
                <div>
                    <Label className="text-xs flex items-center gap-1"><Lock size={10} /> API key {saved && <span className="text-muted-foreground/70 font-mono">(stored: {saved.api_key})</span>}</Label>
                    <Input
                        data-testid={`integration-key-${p.id}`}
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={saved ? "Leave empty to keep stored key" : "Paste API key"}
                        className="rounded-sm font-mono text-xs"
                    />
                </div>
                <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-2">
                        <Switch data-testid={`integration-active-${p.id}`} checked={active} onCheckedChange={setActive} />
                        Active
                    </Label>
                    <div className="flex gap-2">
                        {isApify && (
                            <Button data-testid={`test-${p.id}-btn`} size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => onTest(apiKey)}>
                                Test
                            </Button>
                        )}
                        <Button data-testid={`save-${p.id}-btn`} size="sm" className="rounded-sm h-8 text-xs" onClick={() => onSave({ provider: p.id, api_key: apiKey, active })}>
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </article>
    );
}

export default function Integrations() {
    const [saved, setSaved] = useState({});

    const load = async () => {
        const r = await api.get("/integrations");
        const map = {};
        for (const it of r.data) map[it.provider] = it;
        setSaved(map);
    };

    useEffect(() => { load(); }, []);

    const onSave = async ({ provider, api_key, active }) => {
        try {
            await api.put(`/integrations/${encodeURIComponent(provider)}`, {
                provider,
                api_key,
                active,
                config: saved[provider]?.config || {},
            });
            toast.success(`${provider} saved`);
            await load();
        } catch (e) {
            toast.error(`Failed to save ${provider}`);
        }
    };

    const onTestApify = async (apiKey) => {
        try {
            const r = await api.post(`/integrations/Apify/test`, { api_key: apiKey || null });
            toast.success(`Apify OK · ${r.data.username || "verified"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Apify test failed");
        }
    };

    return (
        <>
            <PageHeader
                overline="Wiring"
                title="Integrations"
                description="Configure ingestion and AI providers. Apify is the primary scraping route; the AI layer routes through the Emergent universal key by default."
                testid="integrations-header"
            />
            <PageBody>
                <div data-testid="integrations-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PROVIDERS.map((p) => (
                        <ProviderCard key={p.id} p={p} saved={saved[p.id]} onSave={onSave} onTest={onTestApify} />
                    ))}
                </div>
            </PageBody>
        </>
    );
}
